// Automatizaciones de la empresa: correos que salen solos. Cada una se guarda como una plantilla con nombre fijo que
// leen las funciones de la app (recordatorios-citas cada 15 min, casos-desde-correo cada 2 min) o la Consola (caso
// resuelto). Solo se envian si estan activas: por defecto todas estan apagadas.
import { create, f, listAll, update } from './rest';

export type ClaveAutomatizacion = 'recordatorio' | 'casoNuevo' | 'fueraHorario' | 'casoResuelto' | 'licenciaES' | 'licenciaEN' | 'licenciaInterna';

export type Automatizacion = { id: string | null; clave: ClaveAutomatizacion; activa: boolean; buzon: string; asunto: string; cuerpo: string; diasPrimer: number | null; diasSegundo: number | null; hora: number | null; destinatarios: string; copiasPrimer: string; copiasSegundo: string; oculta: boolean };

type Definicion = { nombre: string; titulo: string; cuando: string; conBuzon: boolean; variables: string[]; asunto: string; cuerpo: string; programacion?: boolean; interno?: boolean };

const VARS_CASO = ['{{contacto.nombre}}', '{{caso.numero}}', '{{caso.asunto}}'];

export const AUTOMATIZACIONES: Record<ClaveAutomatizacion, Definicion> = {
  recordatorio: {
    nombre: 'Recordatorio de cita',
    titulo: 'Recordatorio de citas',
    cuando: 'Entre 1 y 24 horas antes de cada cita programada o confirmada, el contacto recibe este correo desde el buzón elegido. Si responde, la respuesta llega a ese buzón en Bandeja.',
    conBuzon: true,
    variables: ['{{contacto.nombre}}', '{{cita.titulo}}', '{{cita.fecha}}', '{{cita.hora}}', '{{cita.lugar}}'],
    asunto: 'Recordatorio: {{cita.titulo}} – {{cita.fecha}}',
    cuerpo:
      'Hola {{contacto.nombre}}:\n\nTe recordamos nuestra cita "{{cita.titulo}}" el {{cita.fecha}} a las {{cita.hora}}.\nLugar o enlace: {{cita.lugar}}\n\nSi necesitas cambiarla, responde a este correo.\n\nEquipo XmarteK',
  },
  casoNuevo: {
    nombre: 'Respuesta automatica: caso nuevo',
    titulo: 'Acuse de caso nuevo',
    cuando: 'Cuando un correo abre un caso en un buzón de grupo, el cliente recibe este acuse con su número de caso (en horario de atención). No se envía a remitentes automáticos.',
    conBuzon: false,
    variables: VARS_CASO,
    asunto: '[#{{caso.numero}}] {{caso.asunto}}',
    cuerpo:
      'Hola {{contacto.nombre}}:\n\nRecibimos tu solicitud y abrimos el caso #{{caso.numero}}. Un especialista te responderá pronto.\nPara agregar información, responde a este mismo correo.\n\nEquipo de soporte XmarteK',
  },
  fueraHorario: {
    nombre: 'Respuesta automatica: fuera de horario',
    titulo: 'Acuse fuera de horario',
    cuando: 'Igual que el acuse de caso nuevo, pero para correos que llegan fuera del horario (lunes a viernes de 8:30 a. m. a 6:00 p. m., hora de Colombia).',
    conBuzon: false,
    variables: VARS_CASO,
    asunto: '[#{{caso.numero}}] {{caso.asunto}}',
    cuerpo:
      'Hola {{contacto.nombre}}:\n\nRecibimos tu solicitud y abrimos el caso #{{caso.numero}}. Nuestro horario es de lunes a viernes de 8:30 a. m. a 6:00 p. m. (hora de Colombia); te responderemos en cuanto iniciemos.\n\nEquipo de soporte XmarteK',
  },
  licenciaES: {
    nombre: 'Recordatorio de licencia 3CX (espanol)',
    titulo: 'Licencias 3CX por vencer (español)',
    cuando:
      'Cada Partner con idioma español recibe un correo con la lista de sus licencias por vencer (Ventas → Licencias 3CX): uno en el primer aviso y otro en el segundo. Aquí eliges los días y la hora; valen también para el inglés.',
    conBuzon: true,
    programacion: true,
    variables: ['{{contacto.nombre}}', '{{empresa.nombre}}', '{{dias}}', '{{licencias.tabla}}'],
    asunto: 'Licencias 3CX por vencer en {{dias}} días – {{empresa.nombre}}',
    cuerpo:
      'Estimado(a) {{contacto.nombre}}:\n\nLe escribimos para recordarle que estas licencias 3CX de sus clientes **vencen en los próximos {{dias}} días**:\n\n{{licencias.tabla}}\n\nRenovarlas a tiempo evita que sus clientes pierdan el servicio y las actualizaciones. Si desea la **cotización de la renovación**, basta con responder a este correo y nuestro equipo de licenciamiento se la enviará en este mismo hilo.\n\nSaludos cordiales,',
  },
  licenciaEN: {
    nombre: 'Recordatorio de licencia 3CX (ingles)',
    titulo: 'Licencias 3CX por vencer (inglés)',
    cuando: 'Igual que la versión en español, para los Partners con idioma inglés. Los días y la hora se eligen en la tarjeta en español.',
    conBuzon: true,
    variables: ['{{contacto.nombre}}', '{{empresa.nombre}}', '{{dias}}', '{{licencias.tabla}}'],
    asunto: '3CX licenses expiring in {{dias}} days – {{empresa.nombre}}',
    cuerpo:
      'Dear {{contacto.nombre}},\n\nThis is a reminder that the following 3CX licenses of your customers **expire within the next {{dias}} days**:\n\n{{licencias.tabla}}\n\nRenewing on time keeps your customers’ service and updates running. If you would like a **renewal quote**, simply reply to this email and our licensing team will send it in this same thread.\n\nBest regards,',
  },
  licenciaInterna: {
    nombre: 'Aviso interno de licencias 3CX',
    titulo: 'Aviso interno para cotizar licencias 3CX',
    cuando:
      'Automatización aparte de los avisos a Partners, solo interna: cada día, a su propia hora, quien cotiza recibe las licencias a las que ese día les faltan los días elegidos para vencer, agrupadas por Partner con su contacto y el precio de renovación de 3CX. Cada licencia se avisa una sola vez; si un día no salió el correo, se incluye en el siguiente (hasta una semana).',
    conBuzon: true,
    interno: true,
    variables: ['{{total}}', '{{dias}}', '{{licencias.tabla}}'],
    asunto: 'Cotizar renovación: licencias 3CX que vencen en {{dias}} días ({{total}})',
    cuerpo:
      'Hola Karol:\n\nA estas licencias 3CX les faltan **{{dias}} días** para vencer ({{total}} en total). Por favor prepara la **cotización de renovación** para cada Partner:\n\n{{licencias.tabla}}\n\nEl detalle completo está en el CRM: Ventas → Licencias 3CX.',
  },
  casoResuelto: {
    // La plantilla que lee la funcion casos-desde-correo de la app (antes se buscaba "caso resuelto", que no existe).
    nombre: 'Respuesta automatica: caso cerrado',
    titulo: 'Aviso de caso cerrado',
    cuando: 'Unos minutos después de resolver o cerrar un caso al que el equipo respondió, el cliente recibe este aviso desde el buzón del grupo. Al resolver se puede quitar la casilla «Avisar al cliente». Nunca va a correos de XmarteK ni automáticos.',
    conBuzon: false,
    variables: VARS_CASO,
    asunto: '[#{{caso.numero}}] Resuelto: {{caso.asunto}}',
    cuerpo:
      'Hola {{contacto.nombre}}:\n\nDimos por resuelto el caso #{{caso.numero}}. Si algo sigue pendiente, responde a este correo y lo reabrimos.\n\nEquipo de soporte XmarteK',
  },
};

type Raw = { id: string; name: string; activa: boolean | null; buzon: string | null; asunto: string | null; cuerpo: string | null; diasPrimerAviso: number | null; diasSegundoAviso: number | null; horaEnvio: number | null; destinatarios: string | null; copiasPrimerAviso?: string | null; copiasSegundoAviso?: string | null; oculta?: boolean | null };

export async function automatizaciones(): Promise<Record<ClaveAutomatizacion, Automatizacion>> {
  const nombres = Object.values(AUTOMATIZACIONES).map((d) => d.nombre);
  const items = await listAll<Raw>('plantillasCorreo', { filter: f.in('name', nombres), depth: 0 }, 20);
  const out = {} as Record<ClaveAutomatizacion, Automatizacion>;
  for (const [clave, d] of Object.entries(AUTOMATIZACIONES) as [ClaveAutomatizacion, Definicion][]) {
    const r = items.find((x) => x.name === d.nombre);
    out[clave] = r
      ? { id: r.id, clave, activa: !!r.activa, buzon: r.buzon ?? '', asunto: r.asunto ?? '', cuerpo: r.cuerpo ?? '', diasPrimer: r.diasPrimerAviso, diasSegundo: r.diasSegundoAviso, hora: r.horaEnvio, destinatarios: r.destinatarios ?? '', copiasPrimer: r.copiasPrimerAviso ?? '', copiasSegundo: r.copiasSegundoAviso ?? '', oculta: !!r.oculta }
      : { id: null, clave, activa: false, buzon: '', asunto: d.asunto, cuerpo: d.cuerpo, diasPrimer: null, diasSegundo: null, hora: null, destinatarios: d.interno ? 'kmora@xmartek.com' : '', copiasPrimer: '', copiasSegundo: '', oculta: false };
  }
  return out;
}

// "ana@x.com; Luis@Y.com , " -> "ana@x.com, luis@y.com"
export const normalizarCorreos = (t: string) =>
  t
    .split(/[,;\s]+/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean)
    .join(', ');
export const correosValidos = (t: string) => t.split(/[,;\s]+/).filter(Boolean).every((c) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c));

export async function guardarAutomatizacion(a: Automatizacion) {
  const datos = {
    name: AUTOMATIZACIONES[a.clave].nombre,
    tipo: 'PLANTILLA',
    activa: a.activa && !a.oculta,
    oculta: a.oculta,
    buzon: a.buzon.trim().toLowerCase(),
    asunto: a.asunto,
    cuerpo: a.cuerpo,
    ...(AUTOMATIZACIONES[a.clave].programacion ? { diasPrimerAviso: a.diasPrimer, diasSegundoAviso: a.diasSegundo, horaEnvio: a.hora, copiasPrimerAviso: normalizarCorreos(a.copiasPrimer), copiasSegundoAviso: normalizarCorreos(a.copiasSegundo) } : {}),
    ...(AUTOMATIZACIONES[a.clave].interno ? { diasPrimerAviso: a.diasPrimer, horaEnvio: a.hora, destinatarios: a.destinatarios.trim().toLowerCase() } : {}),
  };
  return a.id ? update('plantillasCorreo', a.id, datos) : create('plantillasCorreo', datos);
}

// Sustitucion de variables (misma regla que las funciones del servidor: lo desconocido queda vacio).
export const rellenar = (texto: string, datos: Record<string, string>) => texto.replace(/\{\{\s*([a-zA-Z]+(?:\.[a-zA-Z]+)?)\s*\}\}/g, (_, k: string) => datos[k] ?? '');

export const EJEMPLO: Record<string, string> = {
  'cita.titulo': 'Demo de 3CX',
  'cita.fecha': 'martes, 29 de septiembre',
  'cita.hora': '9:00 a. m.',
  'cita.lugar': 'meet.google.com/abc-defg-hij',
  'contacto.nombre': 'Ana',
  'caso.numero': '1042',
  'caso.asunto': 'La central no timbra',
  'empresa.nombre': 'Telecom Andina SAS',
  dias: '25',
  total: '3',
  'licencias.tabla': '• ABCD-1234-EFGH-5678 · Professional 8SC · licencia vence el 23 de octubre de 2026 (25 días)\n• WXYZ-9876-QRST-5432 · Enterprise 16SC · mantenimiento vence el 25 de octubre de 2026 (27 días)',
};
